import { convertToRaw } from "draft-js";

import { setMessage } from "actions/message";
import { updateForm, updateFormAt } from "editor/actions/form";
import { STATUS } from "config/constants";
import { updateImageAsync } from "components/library/ImageEditor/actions/imageEditor";
import createAsyncAction from "utils/createAsyncAction";
import { logLocally } from "../../utils/localLogging";
import isTabLockError from "editor/utils/isTabLockError";
import getJsonState from "../../utils/getJsonState";
import Log from "../../utils/log";

export const SET_ACTIVE_EDITOR = "SET_ACTIVE_EDITOR";
export const SET_EDITOR_TYPE = "SET_EDITOR_TYPE";
export const SET_READ_ONLY = "SET_READ_ONLY";
export const EDITOR_SAVE_REQUESTED = "EDITOR_SAVE_REQUESTED";
export const EDITOR_SAVE_SUCCESS = "EDITOR_SAVE_SUCCESS";
export const EDITOR_SAVE_FAILURE = "EDITOR_SAVE_FAILURE";
export const CONTENT_OUT_OF_DATE = "CONTENT_OUT_OF_DATE";
export const DIFFERENT_TAB_LOCKED = "DIFFERENT_TAB_LOCKED";

export const setActiveEditor = (activeEditor, isInlineEditor) => {
  return {
    type: SET_ACTIVE_EDITOR,
    payload: {
      name: activeEditor,
      isInline: isInlineEditor,
    },
  };
};

export const setEditorType = editorType => {
  return {
    type: SET_EDITOR_TYPE,
    payload: {
      name: editorType,
    },
  };
};

export const setReadOnly = isReadOnly => {
  return {
    type: SET_READ_ONLY,
    payload: isReadOnly,
  };
};

export const contentOutOfDate = () => {
  return {
    type: CONTENT_OUT_OF_DATE,
  };
};

export const differentTabLocked = () => {
  return {
    type: DIFFERENT_TAB_LOCKED,
  };
};

export const editorSaveRequested = () => {
  return {
    type: EDITOR_SAVE_REQUESTED,
  };
};

export const editorSaveSuccess = () => {
  return {
    type: EDITOR_SAVE_SUCCESS,
  };
};

export const editorSaveFailure = error => {
  return {
    type: EDITOR_SAVE_FAILURE,
    payload: error,
    error: true,
  };
};

export const setInEdits = () => {
  return (dispatch, getState) => {
    const {
      user,
      editor: { form },
    } = getState();

    // Set the status to in progress with in_edits to true.
    dispatch(
      updateForm({
        status: STATUS.IN_PROGRESS.slug,
        in_edits: true,
      })
    );

    // If the user is in the store, append them to the list of editors.
    if (user && user.id) {
      dispatch(updateFormAt("editors", [...form.editors, user]));
    }

    dispatch(setReadOnly(false));
    return dispatch(saveForm());
  };
};

/**
 * Save the form.
 */
export const saveForm = (rethrowError = false) => {
  return (dispatch, getState, getClient) => {
    const client = getClient(dispatch, getState);
    const {
      editor: { form, status },
    } = getState();

    const body = getJsonState(form);

    if (form.social_image && form.social_image.alt_text) {
      dispatch(
        updateImageAsync(form.social_image.id, {
          alt_text: form.social_image.alt_text,
        })
      );
    }

    /**
     * Subtopic processing
     * -Set a "featured" boolean on subtopics to send back to the API
     * -Clear out any invalid subtopics
     *
     */
    if (form.subtopics && form.subtopics.length > 0) {
      let new_subtopics = form.subtopics
        // Set the "featured" boolean based on featured subtopics list
        // explanation: in the db there's no featured_subtopics field, just
        // a "featured" boolean on subtopics for an article, so we're using
        // the "featured_subtopics" list on the form to set that field here for the db
        .map(subtopic => {
          subtopic.featured = form.featured_subtopics.some(
            featured => featured.id === subtopic.id
          );
          return subtopic;
        })
        // Remove subtopics if the matching topic has been removed on the form
        // explanation: let's try not to have articles assigned to a subtopic but not the topic
        .filter(subtopic => {
          return form.topics.some(topic => topic.id === subtopic.topic);
        });
      dispatch(updateFormAt("subtopics", new_subtopics));

      /**
       * Let's update featured_subtopics to clear any subtopics that were removed
       *
       * note - this change is mostly for clarity so editorial will see the up-to-date list on save;
       * this field doesn't actually persist to the db though and will update accurately on refresh!
       */
      if (form.featured_subtopics && form.featured_subtopics.length > 0) {
        let new_featured_subtopics = form.featured_subtopics.filter(
          featured => {
            return form.subtopics.some(subtopic => featured.id === subtopic.id);
          }
        );
        dispatch(updateFormAt("featured_subtopics", new_featured_subtopics));
      }
    }

    dispatch(editorSaveRequested());

    const requests = [];

    if (form.id) {
      // If the form has an id, it's been saved once before, so add a content update to the request queue
      requests.push(
        client[status.editorType.name].update(form.id, body, status.editKey)
      );
    } else {
      // If the form does not have an ID, the content needs to be created.
      requests.push(
        client[status.editorType.name]
          .create(body)
          .then(response =>
            dispatch(
              updateForm({ id: response.id, head_rev: response.head_rev })
            )
          )
      );
    }

    if (form.id && form.primary_image) {
      // If the form has a primary_image, add an image update to the request queue. This is primarily to save the caption
      requests.push(
        client.images.update(form.primary_image.id, {
          ...form.primary_image,
          caption: convertToRaw(form.primary_image.caption.getCurrentContent()),
        })
      );
    }

    if (form.chunks) {
      form.chunks.forEach(chunk => {
        if (chunk.primary_image) {
          requests.push(
            client.images.update(chunk.primary_image.id, {
              ...chunk.primary_image,
              caption: convertToRaw(
                chunk.primary_image.caption.getCurrentContent()
              ),
            })
          );
        }
      });
    }

    // Execute the entire request queue. Resolve when all requests are finished.
    return Promise.all(requests)
      .then(payloads => {
        const itemPayload = payloads[0];

        // If chunks are in the item payload, patch their IDs into the form.
        if (itemPayload.chunks) {
          itemPayload.chunks.forEach((chunk, i) => {
            if (!form.chunks[i].id) {
              dispatch(updateFormAt(`chunks.${i}.id`, chunk.id));
            }
          });
        }

        // If slug is "", replace slug in the form
        if (!form.slug && itemPayload.slug) {
          dispatch(updateFormAt("slug", itemPayload.slug));
        }

        // don't set head_rev to undefined after the initial save
        itemPayload.head_rev &&
          dispatch(updateFormAt("head_rev", itemPayload.head_rev));

        dispatch(editorSaveSuccess());
      })
      .catch(error => {
        if (
          error &&
          error.message ===
            "There is a newer version of this piece on the server. Please refresh."
        ) {
          dispatch(contentOutOfDate());
        } else if (isTabLockError(error)) {
          dispatch(differentTabLocked());
        } else {
          Log.logger("Save failed", "error", error);
          dispatch(setMessage(error));
        }
        logLocally(error);
        dispatch(editorSaveFailure(error));
        if (rethrowError) {
          throw error;
        }
      });
  };
};

export const publishFormAsync = id => (dispatch, getState, getClient) => {
  const {
    editor: {
      status: {
        editorType: { name },
      },
    },
  } = getState();

  return createAsyncAction(name, "publish", "PUBLISH_FORM")(id)(
    dispatch,
    getState,
    getClient
  );
};

export const scheduleFormAsync = id => (dispatch, getState, getClient) => {
  const {
    editor: {
      status: {
        editorType: { name },
      },
    },
  } = getState();

  return createAsyncAction(name, "schedule", "SCHEDULE_FORM")(id)(
    dispatch,
    getState,
    getClient
  );
};
